import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Header } from "../components/Header";
import { TaskDetailModal } from "../components/TaskDetailModal";
import { statusPill } from "../hooks/useTasks";
import { apiCall } from "../lib/api";
import type { TaskItem } from "../lib/types";

export const Route = createFileRoute("/emails")({
  component: EmailsPage,
});

type EmailFilter = "active" | "dismissed" | "completed";

function extractSubject(emailContent?: string): string {
  if (!emailContent) return "(no subject)";
  const lines = emailContent.split("\n");
  for (const raw of lines) {
    const line = raw.trim();
    if (line.toLowerCase().startsWith("subject:")) return line.substring(8).trim() || "(no subject)";
  }
  for (const raw of lines) {
    const line = raw.trim();
    if (line.length > 0) return line.length > 80 ? line.substring(0, 80) + "..." : line;
  }
  return "(no subject)";
}

function EmailStatusPill({ task }: { task: TaskItem & Record<string, any> }) {
  if (task.dismissed_at) return <span className="task-status-pill dismissed">Dismissed</span>;
  if (task.done && task.status === "sent") return <span className="task-status-pill sent">Sent</span>;
  if (task.done && task.status === "rejected") return <span className="task-status-pill rejected">Rejected</span>;
  if (task.done) return <span className="task-status-pill sent">Completed</span>;
  const p = statusPill(task);
  return p ? <span className={`task-status-pill ${p.cls}`}>{p.label}</span> : null;
}

function emailDate(ts?: string): string {
  if (!ts) return "";
  return new Date(ts + (ts.includes("Z") ? "" : "Z")).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function EmailsPage() {
  const [filter, setFilter] = useState<EmailFilter>("active");
  const [detail, setDetail] = useState<TaskItem | null>(null);

  const { data: tasks = [], isLoading, isError } = useQuery({
    queryKey: ["email-tasks", filter],
    queryFn: () => apiCall<TaskItem[]>(`email-tasks?filter=${filter}`),
  });

  return (
    <>
      <Header variant="emails" />
      <div className="emails-page-content">
        <h1 className="emails-page-title">Email Inbox</h1>
        <div className="emails-filter-bar">
          {(["active", "dismissed", "completed"] as EmailFilter[]).map((f) => (
            <button key={f} className={`emails-filter-btn${filter === f ? " active" : ""}`} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div id="emailsTableContainer">
          {isLoading ? (
            <div className="emails-empty">Loading...</div>
          ) : isError ? (
            <div className="emails-empty">Error loading tasks</div>
          ) : !tasks.length ? (
            <div className="emails-empty">No {filter} email tasks</div>
          ) : (
            <table className="emails-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Subject</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => {
                  const tt = t as TaskItem & Record<string, any>;
                  return (
                    <tr key={t.id} onClick={() => setDetail(t)}>
                      <td>
                        <EmailStatusPill task={tt} />
                      </td>
                      <td className="email-customer">
                        {t.customer_name || "Unknown"}
                        {tt.customer_email && (
                          <>
                            <br />
                            <span style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>{tt.customer_email}</span>
                          </>
                        )}
                      </td>
                      <td className="email-subject">{(tt.subject_extracted as string) || extractSubject(tt.email_content as string | undefined)}</td>
                      <td className="email-date">{emailDate(t.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <TaskDetailModal task={detail} onClose={() => setDetail(null)} />
    </>
  );
}
